export enum ImageDisplayStyle {
  miniatures = 'miniatures',
  fullPage = 'full_page',
  list = 'list',
}

export enum AttachmentsScope {
  all = 'all',
  main = 'main',
}

export interface SectionConfig {
  enabled: boolean;
  displayStyle: ImageDisplayStyle;
}

export interface IssueAttachmentsConfig extends SectionConfig {
  scope: AttachmentsScope;
}

export interface MessageSectionConfig {
  enabled: boolean;
  attachments: SectionConfig;
}

export interface MessagePresentation {
  inEmailNotification: boolean;
  inPdfReport: boolean;
}

export const DEFAULT_SECTION_CONFIG: SectionConfig = {
  enabled: true,
  displayStyle: ImageDisplayStyle.miniatures,
};

export const DEFAULT_ISSUE_ATTACHMENTS_CONFIG: IssueAttachmentsConfig = {
  enabled: true,
  scope: AttachmentsScope.all,
  displayStyle: ImageDisplayStyle.miniatures,
};

export const DEFAULT_MESSAGE_SECTION_CONFIG: MessageSectionConfig = {
  enabled: false,
  attachments: DEFAULT_SECTION_CONFIG,
};

export const DEFAULT_MESSAGE_PRESENTATION: MessagePresentation = {
  inEmailNotification: false,
  inPdfReport: false,
};

export enum ReportAdditionalInformationField {
  showFileNames = 'showFileNames',
  showFileDescriptions = 'showFileDescriptions',
  showActiveLinks = 'showActiveLinks',
  showPhoneNumbers = 'showPhoneNumbers',
  showUserEmails = 'showUserEmails',
}

export enum MessageContent {
  events = 'events',
  incomingEmailMessages = 'incomingEmailMessages',
  workflowDecisions = 'workflowDecisions',
}

export const MESSAGE_CONTENT_VALUES: MessageContent[] = Object.values(MessageContent);

export enum ReportFieldsContent {
  all = 'all_fields',
  filled = 'only_fields_with_value',
  selected = 'only_selected_fields',
}

type ReportFieldsContentLabelsType = Record<ReportFieldsContent, string>;

export const REPORT_FIELDS_CONTENT_LABELS: ReportFieldsContentLabelsType = {
  [ReportFieldsContent.all]: 'report_field_content_all_fields' as const,
  [ReportFieldsContent.filled]: 'report_field_content_only_fields_with_value' as const,
  [ReportFieldsContent.selected]: 'report_field_content_only_selected_fields' as const,
};

export enum BaseReportFormField {
  recipients = 'recipients',
  name = 'name',
  message = 'message',
  messagePresentation = 'messagePresentation',
  localeCode = 'localeCode',
}

export enum IssueReportFormField {
  fieldsContent = 'fieldsContent',
  selectedFields = 'selectedFields',
}

export interface BaseReportModel {
  recipients: string[];
  name: string;
  message: string;
  messagePresentation: MessagePresentation;
  localeCode: string;
  showPhoneNumbers: boolean;
  showUserEmails: boolean;
  showFileNames: boolean;
  showFileDescriptions: boolean;
  showActiveLinks: boolean;
  issueAttachments: IssueAttachmentsConfig;
}

export enum ReportType {
  issueList = 'issueList',
  inspection = 'inspection',
  singleIssue = 'singleIssue',
}

/** Issue reports only — excludes {@link ReportType.inspection}. */
export type IssueReportType = Exclude<ReportType, ReportType.inspection>;

export interface InspectionReportParts {
  inspectionInfo: boolean;
  summary: boolean;
  checklist: boolean;
  issueRegister: boolean;
  signatures: boolean;
}

export interface InspectionTemplateData {
  generalStandard: boolean;
  generalStandardAttachments: SectionConfig;
  sectionDescriptions: boolean;
  sectionAttachments: SectionConfig;
  controlledElementStandard: boolean;
  controlledElementStandardAttachments: SectionConfig;
}

export interface InspectionDataOptions {
  controlledElementAttachments: SectionConfig;
  showNotApplicable: boolean;
  showUnchecked: boolean;
}

export const DEFAULT_INSPECTION_DISABLED_SECTION_CONFIG: SectionConfig = {
  enabled: false,
  displayStyle: ImageDisplayStyle.miniatures,
};

export const DEFAULT_INSPECTION_REPORT_PARTS: InspectionReportParts = {
  inspectionInfo: true,
  summary: true,
  checklist: true,
  issueRegister: true,
  signatures: true,
};

export const DEFAULT_INSPECTION_TEMPLATE_DATA: InspectionTemplateData = {
  generalStandard: false,
  generalStandardAttachments: { ...DEFAULT_INSPECTION_DISABLED_SECTION_CONFIG },
  sectionDescriptions: true,
  sectionAttachments: { ...DEFAULT_SECTION_CONFIG },
  controlledElementStandard: true,
  controlledElementStandardAttachments: { ...DEFAULT_SECTION_CONFIG },
};

export const DEFAULT_INSPECTION_DATA_OPTIONS: InspectionDataOptions = {
  controlledElementAttachments: {
    enabled: true,
    displayStyle: ImageDisplayStyle.miniatures,
  },
  showNotApplicable: true,
  showUnchecked: true,
};

/** Master toggles for inspection report generator v2 (TDD 2026-08-11). */
export interface InspectionReportSectionMasters {
  templateSectionEnabled: boolean;
  checklistSectionEnabled: boolean;
  issueDataSectionEnabled: boolean;
}

export const DEFAULT_INSPECTION_SECTION_MASTERS: InspectionReportSectionMasters =
  {
    templateSectionEnabled: false,
    checklistSectionEnabled: true,
    issueDataSectionEnabled: true,
  };

export const DEFAULT_INSPECTION_LINK_TO_HUSTRO = true;
